import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { createPortal } from "react-dom";
import { ArrowsClockwise, Boot, CaretDown, CaretUp, Crown, Eye, EyeSlash, Microphone, MicrophoneSlash, PaperPlaneTilt, Prohibit, Student, Users, UsersThree } from "@phosphor-icons/react";
import type { ModerationAction, RoomUser } from "@yay/shared";
import { ChatText } from "./ChatText";
import { Censored } from "./Censored";

interface AvatarTheaterProps {
  users: RoomUser[];
  me: RoomUser | null;
  lastError: string | null;
  profileSyncing?: boolean;
  onRename: (username: string) => boolean;
  onReroll: () => boolean;
  onClearError: () => void;
  collapsed: boolean;
  onToggleTheater: () => void;
  /** When true, header says "studying" and chat bubbles appear on avatars. */
  nerdMode?: boolean;
  /** Theater collapsed: bubbles scatter instead of anchoring to avatars. */
  scatterBubbles?: boolean;
  /** Scatter across the whole viewport (full-nerd). Otherwise stay in the theater column. */
  scatterFullscreen?: boolean;
  /** Recent chat messages used to spawn ephemeral speech bubbles. */
  bubbleMessages?: import("@yay/shared").ChatBroadcastMessage[];
  /** User id of the study leader being followed, if any. */
  followingUserId?: string | null;
  onFollow?: (userId: string) => void;
  /** Slash system (Phase 1.3) — owner moderation + whisper from the avatar menu. */
  isOwner?: boolean;
  /** Whether the owner holds the Pro entitlement (authoritative
   * moderation). When false, kick/ban route to a room vote and mute/
   * transfer are refused — the menu must not promise them as instant. */
  ownerPro?: boolean;
  /** Private room (votes only work there — the lobby is strict). */
  inPrivateRoom?: boolean;
  mutedIds?: string[];
  onCommand?: (action: ModerationAction, targetIds: string[]) => boolean;
  onWhisper?: (targetUserId: string, text: string) => boolean;
  onBlock?: (username: string) => void;
  isBlocked?: (username: string) => boolean;
}

type SpeechBubble = {
  text: string;
  key: string;
  fading: boolean;
  /** Viewport % for scatter mode */
  top?: number;
  left?: number;
};

function firstName(username: string) {
  return username.split(" ")[0];
}

function avatarUrl(user: RoomUser) {
  return `https://api.dicebear.com/10.x/dylan/svg?seed=${encodeURIComponent(user.avatarSeed)}`;
}

function rectsOverlap(
  a: { left: number; top: number; right: number; bottom: number },
  b: DOMRect,
): boolean {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

/** Expand a rect so we keep clear of the content core (edges may still graze). */
function expandRect(r: DOMRect, pad: number): DOMRect {
  return new DOMRect(
    r.left - pad,
    r.top - pad,
    r.width + pad * 2,
    r.height + pad * 2,
  );
}

function pctToBox(
  host: DOMRect,
  topPct: number,
  leftPct: number,
  bubbleW: number,
  bubbleH: number,
) {
  const x = host.left + (leftPct / 100) * host.width;
  const y = host.top + (topPct / 100) * host.height;
  return { left: x, top: y, right: x + bubbleW, bottom: y + bubbleH, topPct, leftPct };
}

/**
 * Place bubbles in free purple margins — never bias toward viewport center
 * (that's where flashcard text lives in full-nerd).
 */
function pickScatterPosition(
  host: Element | null,
  fullscreen: boolean,
): { top: number; left: number } {
  const hostRect = fullscreen
    ? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
    : (host?.getBoundingClientRect() ??
      new DOMRect(0, 0, window.innerWidth, window.innerHeight));

  // Hard-avoid text + video cores (padded). Do NOT forbid the whole flash face —
  // empty purple around the text is exactly where bubbles should live.
  const pad = Math.min(40, Math.max(20, hostRect.width * 0.03));
  const forbidden = [
    ...document.querySelectorAll<HTMLElement>(
      ".nerd-flash-text, .player-container, .youtube-mount",
    ),
  ]
    .map((el) => expandRect(el.getBoundingClientRect(), pad))
    .filter((r) => r.width > 0 && r.height > 0);

  const bubbleW = Math.min(200, hostRect.width * 0.35);
  const bubbleH = 72;
  const rim = 12; // % — stay off the outer edge
  const span = 100 - rim * 2;

  const tryPos = (topPct: number, leftPct: number) => {
    const box = pctToBox(hostRect, topPct, leftPct, bubbleW, bubbleH);
    if (forbidden.some((r) => rectsOverlap(box, r))) return null;
    return { top: topPct, left: leftPct };
  };

  // Uniform samples in the inset area (no center bias).
  for (let attempt = 0; attempt < 40; attempt++) {
    const hit = tryPos(rim + Math.random() * span, rim + Math.random() * span);
    if (hit) return hit;
  }

  // Prefer empty bands relative to the flashcard text, if present.
  const textEl = document.querySelector<HTMLElement>(".nerd-flash-text");
  if (textEl) {
    const t = textEl.getBoundingClientRect();
    const bands: Array<{ topPct: number; leftPct: number }> = [];
    const toPctY = (y: number) =>
      ((y - hostRect.top) / Math.max(1, hostRect.height)) * 100;
    const toPctX = (x: number) =>
      ((x - hostRect.left) / Math.max(1, hostRect.width)) * 100;

    // Above text
    if (t.top - hostRect.top > bubbleH + 24) {
      for (let i = 0; i < 6; i++) {
        bands.push({
          topPct: Math.min(
            toPctY(hostRect.top + 16 + Math.random() * Math.max(8, t.top - hostRect.top - bubbleH - 24)),
            88,
          ),
          leftPct: rim + 10 + Math.random() * (span - 20),
        });
      }
    }
    // Below text
    if (hostRect.bottom - t.bottom > bubbleH + 24) {
      for (let i = 0; i < 6; i++) {
        bands.push({
          topPct: Math.max(
            toPctY(t.bottom + 16 + Math.random() * Math.max(8, hostRect.bottom - t.bottom - bubbleH - 24)),
            rim,
          ),
          leftPct: rim + 10 + Math.random() * (span - 20),
        });
      }
    }
    // Left / right of text
    if (t.left - hostRect.left > bubbleW + 16) {
      for (let i = 0; i < 4; i++) {
        bands.push({
          topPct: toPctY(t.top + Math.random() * Math.max(8, t.height - 20)),
          leftPct: toPctX(hostRect.left + 12 + Math.random() * Math.max(8, t.left - hostRect.left - bubbleW - 16)),
        });
      }
    }
    if (hostRect.right - t.right > bubbleW + 16) {
      for (let i = 0; i < 4; i++) {
        bands.push({
          topPct: toPctY(t.top + Math.random() * Math.max(8, t.height - 20)),
          leftPct: toPctX(t.right + 12 + Math.random() * Math.max(8, hostRect.right - t.right - bubbleW - 16)),
        });
      }
    }

    for (const b of bands.sort(() => Math.random() - 0.5)) {
      const hit = tryPos(b.topPct, b.leftPct);
      if (hit) return hit;
    }
  }

  // Last resort: corners / lower theater band — never dead-center.
  const fallbacks = [
    { top: 14, left: 18 },
    { top: 14, left: 62 },
    { top: 72, left: 22 },
    { top: 72, left: 55 },
    { top: 78, left: 38 },
  ];
  const pick = fallbacks[Math.floor(Math.random() * fallbacks.length)]!;
  return pick;
}

function AvatarFallback({ user, className }: { user: RoomUser; className: string }) {
  return (
    <div
      className={className}
      aria-label={user.username}
      style={{
        background: user.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 700,
        fontSize: 20,
        lineHeight: 1,
      }}
    >
      {user.username[0]?.toUpperCase()}
    </div>
  );
}

function usersShallowEqual(a: RoomUser[], b: RoomUser[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i]!;
    const right = b[i]!;
    if (
      left.id !== right.id ||
      left.username !== right.username ||
      left.color !== right.color ||
      left.avatarSeed !== right.avatarSeed
    ) {
      return false;
    }
  }
  return true;
}

function meEqual(a: RoomUser | null, b: RoomUser | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.username === b.username &&
    a.color === b.color &&
    a.avatarSeed === b.avatarSeed
  );
}

export const AvatarTheater = memo(function AvatarTheater({
  users,
  me,
  lastError,
  profileSyncing = false,
  onRename,
  onReroll,
  onClearError,
  collapsed,
  onToggleTheater,
  nerdMode = false,
  scatterBubbles = false,
  scatterFullscreen = false,
  bubbleMessages = [],
  followingUserId = null,
  onFollow,
  isOwner = false,
  ownerPro = false,
  inPrivateRoom = false,
  mutedIds = [],
  onCommand,
  onWhisper,
  onBlock,
  isBlocked,
}: AvatarTheaterProps) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPosition, setEditPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [gridMaxHeight, setGridMaxHeight] = useState<number | null>(null);
  const [localError, setLocalError] = useState("");
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  /** Username at the moment a rename was submitted — server may suffix it. */
  const origNameRef = useRef<string | null>(null);
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());
  /** Right-click / long-press avatar action menu (slash system). */
  const [actionMenu, setActionMenu] = useState<{
    user: RoomUser;
    cx: number;
    cy: number;
    left: number;
    right: number;
    top: number;
    bottom: number;
  } | null>(null);
  const [menuBox, setMenuBox] = useState<{
    top: number;
    left: number;
    tail: "top" | "bottom" | "left" | "right";
    tailX: number;
    tailY: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuWhisper, setMenuWhisper] = useState("");
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Two-tap confirm for destructive actions (kick/ban) — no instant misclicks. */
  const [confirmCmd, setConfirmCmd] = useState<{
    id: string;
    action: string;
  } | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function armConfirm(id: string, action: string) {
    if (confirmTimerRef.current !== null) {
      clearTimeout(confirmTimerRef.current);
    }
    setConfirmCmd({ id, action });
    confirmTimerRef.current = setTimeout(() => {
      confirmTimerRef.current = null;
      setConfirmCmd(null);
    }, 3_000);
  }

  function closeActionMenu() {
    if (confirmTimerRef.current !== null) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    setConfirmCmd(null);
    setActionMenu(null);
    setMenuBox(null);
  }

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => closeActionMenu();
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
    });
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", (e) => {
        if (e.key === "Escape") close();
      });
    };
  }, [actionMenu]);

  function openActionMenu(e: ReactMouseEvent | ReactTouchEvent, user: RoomUser) {
    e.preventDefault();
    if (user.id === me?.id) return;
    const target = e.currentTarget as HTMLElement;
    const r = target.getBoundingClientRect();
    setActionMenu({
      user,
      cx: r.left + r.width / 2,
      cy: r.top + r.height / 2,
      left: r.left,
      right: r.right,
      top: r.top,
      bottom: r.bottom,
    });
    setMenuWhisper("");
    setMenuBox(null);
  }

  useLayoutEffect(() => {
    if (!actionMenu) return;
    const el = menuRef.current;
    if (!el) return;
    const pad = 10;
    const gap = 10;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { cx, cy, left: aLeft, right: aRight, top: aTop, bottom: aBottom } =
      actionMenu;
    const spaceRight = vw - aRight - pad;
    const spaceLeft = aLeft - pad;
    const spaceBelow = vh - aBottom - pad;
    const spaceAbove = aTop - pad;
    const need = w + gap;
    const canRight = spaceRight >= need;
    const canLeft = spaceLeft >= need;

    let top: number;
    let left: number;
    let tail: "top" | "bottom" | "left" | "right";
    let tailX = 18;
    let tailY = 18;

    if (canRight || (!canLeft && spaceRight >= spaceLeft && spaceRight >= 48)) {
      left = aRight + gap;
      top = cy - h / 2;
      tail = "left";
    } else if (canLeft || spaceLeft >= 48) {
      left = aLeft - w - gap;
      top = cy - h / 2;
      tail = "right";
    } else {
      const placeAbove = spaceAbove >= h + gap || spaceAbove >= spaceBelow;
      tail = placeAbove ? "bottom" : "top";
      top = placeAbove ? aTop - h - gap : aBottom + gap;
      left = cx - w / 2;
    }

    left = Math.max(pad, Math.min(left, vw - w - pad));
    top = Math.max(pad, Math.min(top, vh - h - pad));
    tailX = Math.max(14, Math.min(cx - left, w - 14));
    tailY = Math.max(14, Math.min(cy - top, h - 14));
    setMenuBox({ top, left, tail, tailX, tailY });
  }, [actionMenu, confirmCmd]);
  // Only show spinner/spin after a brief delay — fast operations shouldn't flicker.
  const [syncing, setSyncing] = useState(false);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLFormElement>(null);
  const theaterRef = useRef<HTMLDivElement>(null);
  /** User ids that should play the enter animation (joiners only, once). */
  const [enteringIds, setEnteringIds] = useState<Set<string>>(() => new Set());
  const knownUserIdsRef = useRef<Set<string> | null>(null);
  const [heightAnimating, setHeightAnimating] = useState(false);
  const heightAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (lastError) setLocalError(lastError);
  }, [lastError]);

  useEffect(() => {
    if (!pendingRef.current || !editing || !me) return;
    if (me.username !== origNameRef.current) {
      // Server confirmed the rename (it may add a numeric suffix when the
      // name is taken) — close the panel.
      pendingRef.current = false;
      setPending(false);
      setEditing(false);
      setLocalError("");
      onClearError();
    } else if (!profileSyncing) {
      // The rename never applied (timeout / server error) — stop the spinner
      // but keep the panel open so the user can correct it.
      pendingRef.current = false;
      setPending(false);
    }
  }, [me, editing, profileSyncing, onClearError]);

  // close when theater collapses
  useEffect(() => {
    if (collapsed && editing) closeEdit();
  }, [collapsed]);

  // Collapse/expand: animate max-height. Presence churn must NOT tween through 0
  // or the whole theater looks like it "reappears" after idle wake / rejoin.
  useEffect(() => {
    if (collapsed) {
      setGridMaxHeight(0);
      return;
    }
    const measure = () => {
      if (!gridRef.current) return;
      const h = gridRef.current.scrollHeight;
      // Keep prior height if the grid is briefly empty (reconnect blip).
      if (h === 0) return;
      setGridMaxHeight(h);
    };
    requestAnimationFrame(measure);
  }, [collapsed]);

  useEffect(() => {
    if (collapsed) return;
    if (!gridRef.current) return;
    const h = gridRef.current.scrollHeight;
    if (h === 0) return;
    // Presence size changes: snap height without relying on 0→N expand anim.
    setGridMaxHeight(h);
  }, [collapsed, users.length]);

  // close on outside click
  useEffect(() => {
    if (!editing) return;
    function onDown(e: MouseEvent) {
      if (
        editRef.current &&
        !editRef.current.contains(e.target as Node) &&
        meRef.current &&
        !meRef.current.contains(e.target as Node)
      ) {
        closeEdit();
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editing]);

  // Enter animation only for *other* people who newly appear — never for self,
  // and never reset the known set when presence briefly empties on wake/rejoin.
  useEffect(() => {
    const nextIds = new Set(users.map((u) => u.id));
    if (me) nextIds.add(me.id);

    if (knownUserIdsRef.current === null) {
      knownUserIdsRef.current = new Set(nextIds);
      return;
    }

    const known = knownUserIdsRef.current;
    const newcomers: string[] = [];
    for (const id of nextIds) {
      if (known.has(id)) continue;
      known.add(id);
      // Don't animate your own seat after session rebuild / rejoin.
      if (me && id === me.id) continue;
      newcomers.push(id);
    }

    if (newcomers.length === 0) return;
    setEnteringIds(new Set(newcomers));
    const timer = window.setTimeout(() => setEnteringIds(new Set()), 400);
    return () => window.clearTimeout(timer);
  }, [users, me]);

  function placeEditPanel() {
    if (!meRef.current) return;
    const meRect = meRef.current.getBoundingClientRect();
    const panelW = 280;
    const panelH = 140;
    const gap = 12;
    const pad = 12;
    const content =
      theaterRef.current?.closest(".content") ??
      theaterRef.current?.closest(".main-layout");
    const bounds = content?.getBoundingClientRect() ?? {
      left: pad,
      right: window.innerWidth - pad,
      top: pad,
      bottom: window.innerHeight - pad,
    };
    const minLeft = bounds.left + pad;
    const maxRight = Math.min(window.innerWidth - pad, bounds.right - pad);
    const maxBottom = Math.min(window.innerHeight - pad, bounds.bottom - pad);

    let left = meRect.right + gap;
    let top = meRect.top;

    if (left + panelW > maxRight) {
      left = meRect.left - panelW - gap;
    }
    if (left < minLeft) {
      left = minLeft;
    }
    if (left + panelW > maxRight) {
      left = Math.max(minLeft, maxRight - panelW);
    }

    if (top + panelH > maxBottom) {
      top = Math.max(bounds.top + pad, maxBottom - panelH);
    }
    if (top < bounds.top + pad) top = bounds.top + pad;

    setEditPosition({ top, left });
  }

  useLayoutEffect(() => {
    if (!editing) return;
    placeEditPanel();
    function onReposition() {
      placeEditPanel();
    }
    window.addEventListener("resize", onReposition);
    // Theater column scrolls inside .left-col-scroll
    const scrollRoot =
      theaterRef.current?.closest(".left-col-scroll") ??
      theaterRef.current?.closest(".left-col");
    scrollRoot?.addEventListener("scroll", onReposition, { passive: true });
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      scrollRoot?.removeEventListener("scroll", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [editing, collapsed, nerdMode]);

  function openEdit() {
    if (!me) return;
    setEditName(me.username);
    setLocalError("");
    onClearError();
    pendingRef.current = false;
    setEditing(true);
  }

  function handleToggle() {
    if (heightAnimTimerRef.current) {
      clearTimeout(heightAnimTimerRef.current);
    }
    setHeightAnimating(true);
    heightAnimTimerRef.current = setTimeout(() => {
      setHeightAnimating(false);
      heightAnimTimerRef.current = null;
    }, 400);

    if (!collapsed && gridRef.current) {
      setGridMaxHeight(gridRef.current.scrollHeight);
      requestAnimationFrame(() => onToggleTheater());
    } else {
      onToggleTheater();
    }
  }

  function closeEdit() {
    pendingRef.current = false;
    setPending(false);
    setEditing(false);
    setEditPosition(null);
  }

  function handleRename(e: FormEvent) {
    e.preventDefault();
    const trimmed = editName.trim();
    if (!trimmed || trimmed === me?.username) {
      closeEdit();
      return;
    }
    setLocalError("");
    const ok = onRename(trimmed);
    if (ok) {
      origNameRef.current = me?.username ?? null;
      pendingRef.current = true;
      setPending(true);
    } else {
      setLocalError("Connection lost — try again");
    }
  }

  // When the user list refreshes (new presence), clear failed-avatar state so
  // cards whose DiceBear request previously failed get another chance to load.
  // Compare by id set — not array identity — so room_state re-renders don't
  // wipe fallbacks and flash every avatar.
  const usersIdKey = users.map((u) => u.id).join("|") + (me ? `|${me.id}` : "");
  const prevUsersIdKey = useRef(usersIdKey);
  useEffect(() => {
    if (prevUsersIdKey.current === usersIdKey) return;
    prevUsersIdKey.current = usersIdKey;
    setFailedAvatars(new Set());
  }, [usersIdKey]);

  // Mirror pending/profileSyncing into syncing with a delay so the spinner
  // only appears during actual delays, not on fast round-trips.
  useEffect(() => {
    const active = pending || profileSyncing;
    if (active) {
      if (!syncTimerRef.current) {
        syncTimerRef.current = setTimeout(() => {
          syncTimerRef.current = null;
          setSyncing(true);
        }, 400);
      }
    } else {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      setSyncing(false);
    }
  }, [pending, profileSyncing]);

  const onAvatarError = useCallback((userId: string) => {
    setFailedAvatars((prev) => {
      if (prev.has(userId)) return prev;
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
  }, []);

  function handleReroll() {
    setLocalError("");
    const ok = onReroll();
    if (!ok) setLocalError("Connection lost — try again");
  }

  /** Ephemeral nerd speech bubbles — pop in, hold, fade out (game-style). */
  const [speechBubbles, setSpeechBubbles] = useState<Map<string, SpeechBubble>>(
    () => new Map(),
  );
  const lastBubbleKey = useRef<string | null>(null);
  const wasNerd = useRef(false);
  const scatterRef = useRef(scatterBubbles);
  scatterRef.current = scatterBubbles;
  const [scatterHost, setScatterHost] = useState<Element | null>(null);

  const scatterFullscreenRef = useRef(scatterFullscreen);
  scatterFullscreenRef.current = scatterFullscreen;

  useEffect(() => {
    if (!scatterBubbles || scatterFullscreen) {
      setScatterHost(null);
      return;
    }
    const host = theaterRef.current?.closest(".left-col") ?? null;
    setScatterHost(host);
  }, [scatterBubbles, scatterFullscreen, collapsed]);

  useEffect(() => {
    if (!nerdMode) {
      setSpeechBubbles(new Map());
      lastBubbleKey.current = null;
      wasNerd.current = false;
      return;
    }

    const keyOf = (m: (typeof bubbleMessages)[number]) =>
      `${m.id}-${m.timestamp}`;
    const latest = bubbleMessages[bubbleMessages.length - 1];
    const latestKey = latest ? keyOf(latest) : null;

    // Entering nerd: sync cursor, don't replay chat history as bubbles.
    if (!wasNerd.current) {
      lastBubbleKey.current = latestKey;
      wasNerd.current = true;
      return;
    }

    if (!latest || latestKey === lastBubbleKey.current) return;

    const prevKey = lastBubbleKey.current;
    const startIdx = prevKey
      ? bubbleMessages.findIndex((m) => keyOf(m) === prevKey)
      : -1;
    const incoming =
      startIdx >= 0
        ? bubbleMessages.slice(startIdx + 1)
        : bubbleMessages.slice(-1);

    lastBubbleKey.current = latestKey;

    for (const msg of incoming) {
      const key = keyOf(msg);
      const username = msg.username;
      const scatter = scatterRef.current;
      const fullscreen = scatterFullscreenRef.current;
      const host = scatter
        ? fullscreen
          ? null
          : (theaterRef.current?.closest(".left-col") ?? null)
        : null;
      const pos = scatter
        ? pickScatterPosition(host, fullscreen)
        : { top: undefined, left: undefined };

      setSpeechBubbles((prev) => {
        const next = new Map(prev);
        next.set(username, {
          text: msg.text,
          key,
          fading: false,
          top: pos.top,
          left: pos.left,
        });
        return next;
      });

      window.setTimeout(() => {
        setSpeechBubbles((prev) => {
          const cur = prev.get(username);
          if (!cur || cur.key !== key) return prev;
          const next = new Map(prev);
          next.set(username, { ...cur, fading: true });
          return next;
        });
        window.setTimeout(() => {
          setSpeechBubbles((prev) => {
            const cur = prev.get(username);
            if (!cur || cur.key !== key) return prev;
            const next = new Map(prev);
            next.delete(username);
            return next;
          });
        }, 380);
      }, 4200);
    }
  }, [bubbleMessages, nerdMode]);

  // Drop in-flight bubbles when switching scatter ↔ avatar anchoring.
  const prevScatter = useRef(scatterBubbles);
  useEffect(() => {
    if (!nerdMode) return;
    if (prevScatter.current === scatterBubbles) return;
    prevScatter.current = scatterBubbles;
    setSpeechBubbles(new Map());
  }, [scatterBubbles, nerdMode]);

  const sorted =
    me && (users.length === 0 || users.some((u) => u.id === me.id))
      ? [me, ...users.filter((u) => u.id !== me.id)]
      : users;

  // Mirrors the server's authoritative gate (ownerId + isPro || present ≤ 2):
  // when false, the owner's kick/ban route to a room vote and mute/transfer
  // are refused — the menu labels and gates below must reflect that.
  const ownerDirect = isOwner && (ownerPro || users.length <= 2);

  // Center the grid row when avatars don't fill a full first row.
  const [centerSingleRow, setCenterSingleRow] = useState(false);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const cardW = 72;
    const gap = 16;
    const seatW = cardW + gap;
    const measure = () => {
      const cols = Math.max(1, Math.floor((el.clientWidth + gap) / seatW));
      setCenterSingleRow(sorted.length <= cols);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sorted.length, collapsed]);

  if (users.length === 0 && !me) {
    return (
      <div className="theater">
        <p className="theater-empty">No one here yet. Be the first!</p>
      </div>
    );
  }

  const error = localError;

  const scatterLayer =
    nerdMode && scatterBubbles && speechBubbles.size > 0 ? (
      <div
        className={`nerd-scatter-bubbles${scatterFullscreen ? " fullscreen" : ""}`}
        aria-live="polite"
      >
        {[...speechBubbles.entries()].map(([username, speech]) => (
          <div
            key={speech.key}
            className={`nerd-speech-bubble scatter${speech.fading ? " fading" : ""}`}
            style={{ top: `${speech.top ?? 20}%`, left: `${speech.left ?? 20}%` }}
            title={`${username}: ${speech.text}`}
          >
            <span className="nerd-speech-name">{firstName(username)}</span>
            <span className="nerd-speech-text">
              <ChatText text={speech.text} compact stickersOnly />
            </span>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div
      ref={theaterRef}
      className={`theater${collapsed ? " collapsed" : ""}${nerdMode ? " nerd" : ""}`}
      aria-label="Viewers in the room"
    >
      {scatterFullscreen && scatterLayer}
      {!scatterFullscreen &&
        scatterLayer &&
        scatterHost &&
        createPortal(scatterLayer, scatterHost)}

      <div className="theater-cluster">
      <div className="theater-header">
        <Users size={16} weight="fill" color="#bf94ff" />
        <span className="theater-count">{users.length.toLocaleString()}</span>
        <span className="theater-label">{nerdMode ? "studying" : "watching"}</span>
        <button
          className="theater-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? "Show viewers" : "Hide viewers"}
        >
          {collapsed ? <CaretUp size={14} weight="fill" /> : <CaretDown size={14} weight="fill" />}
        </button>
      </div>

      <div className="theater-body">
        <div
          className={`theater-grid-wrap${heightAnimating ? " animating" : ""}`}
          style={{ maxHeight: collapsed ? 0 : (gridMaxHeight ?? 800) }}
        >
          <div className={`theater-grid${centerSingleRow ? " center-single-row" : ""}`} ref={gridRef}>
          {sorted.map((user) => {
            const isMe = me !== null && me.id === user.id;
            const isFollowing = followingUserId === user.id;
            const speech =
              nerdMode && !scatterBubbles && !collapsed
                ? speechBubbles.get(user.username)
                : undefined;
            return (
              <div
                key={user.id}
                className={`theater-card${isMe ? " is-me" : ""}${isFollowing ? " following" : ""}${enteringIds.has(user.id) ? " is-entering" : ""}`}
                title={user.username}
                ref={isMe ? meRef : undefined}
              >
                {!isMe && nerdMode && onFollow && (
                  <button
                    type="button"
                    className={`theater-follow-btn${isFollowing ? " active" : ""}`}
                    aria-label={
                      isFollowing
                        ? `Following ${user.username}`
                        : `Follow ${user.username}'s cards`
                    }
                    title={
                      isFollowing
                        ? `Following ${firstName(user.username)}`
                        : `Follow ${firstName(user.username)}`
                    }
                    onClick={() => onFollow(user.id)}
                  >
                    <UsersThree size={14} weight="bold" />
                  </button>
                )}
                <button
                  className="theater-avatar-btn"
                  onClick={
                    isMe
                      ? () => (editing ? closeEdit() : openEdit())
                      : (e) => openActionMenu(e, user)
                  }
                  onContextMenu={(e) => openActionMenu(e, user)}
                  onTouchStart={
                    isMe
                      ? undefined
                      : (e) => {
                          longPressTimer.current = setTimeout(
                            () => openActionMenu(e, user),
                            600,
                          );
                        }
                  }
                  onTouchEnd={() => {
                    if (longPressTimer.current) {
                      clearTimeout(longPressTimer.current);
                      longPressTimer.current = null;
                    }
                  }}
                  onTouchMove={() => {
                    if (longPressTimer.current) {
                      clearTimeout(longPressTimer.current);
                      longPressTimer.current = null;
                    }
                  }}
                  aria-label={isMe ? "Edit your profile" : user.username}
                  type="button"
                >
                  {speech && (
                    <span
                      className={`nerd-speech-bubble${speech.fading ? " fading" : ""}`}
                      title={speech.text}
                    >
                      <span className="nerd-speech-text">
                        <ChatText text={speech.text} compact stickersOnly />
                      </span>
                    </span>
                  )}
                  {failedAvatars.has(user.id) ? (
                    <AvatarFallback className="theater-avatar" user={user} />
                  ) : (
                    <img
                      className="theater-avatar"
                      src={avatarUrl(user)}
                      alt={user.username}
                      onError={() => onAvatarError(user.id)}
                    />
                  )}
                  {isMe && <span className="theater-you-badge">you</span>}
                </button>
                <span className="theater-name">
                  <Censored text={firstName(user.username)} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {actionMenu &&
        actionMenu.user.id !== me?.id &&
        createPortal(
          <div
            ref={menuRef}
            className="avatar-action-menu"
            data-tail={menuBox?.tail ?? "left"}
            style={{
              top: menuBox?.top ?? actionMenu.top,
              left: menuBox?.left ?? actionMenu.right,
              visibility: menuBox ? "visible" : "hidden",
              ["--tail-x" as string]: `${menuBox?.tailX ?? 18}px`,
              ["--tail-y" as string]: `${menuBox?.tailY ?? 18}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="avatar-action-menu-head">
              <Censored text={actionMenu.user.username} />
            </div>
            <div className="avatar-action-whisper">
              <input
                type="text"
                placeholder={`to ${actionMenu.user.username}…`}
                value={menuWhisper}
                maxLength={200}
                aria-label={`Whisper to ${actionMenu.user.username}`}
                onChange={(e) => setMenuWhisper(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && menuWhisper.trim()) {
                    onWhisper?.(actionMenu.user.id, menuWhisper.trim());
                    closeActionMenu();
                  }
                }}
              />
              <button
                type="button"
                className="avatar-action-whisper-send"
                disabled={!menuWhisper.trim()}
                aria-label="Send whisper"
                onClick={() => {
                  onWhisper?.(actionMenu.user.id, menuWhisper.trim());
                  closeActionMenu();
                }}
              >
                <PaperPlaneTilt size={15} weight="bold" />
              </button>
            </div>
            {onFollow && (
              <button
                type="button"
                className={`avatar-action-item${followingUserId === actionMenu.user.id ? " active" : ""}`}
                onClick={() => {
                  onFollow(actionMenu.user.id);
                  closeActionMenu();
                }}
              >
                {followingUserId === actionMenu.user.id ? (
                  <EyeSlash size={15} weight="bold" />
                ) : (
                  <Student size={15} weight="bold" />
                )}
                <span>
                  {followingUserId === actionMenu.user.id
                    ? "unfollow"
                    : "follow"}
                </span>
              </button>
            )}
            {isOwner && (
              <>
                <button
                  type="button"
                  className={`avatar-action-item${confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "transfer" ? " confirm" : ""}`}
                  disabled={!ownerDirect}
                  title={ownerDirect ? undefined : "Pro feature — not available yet"}
                  onClick={() => {
                    if (ownerDirect) {
                      if (
                        confirmCmd?.id === actionMenu.user.id &&
                        confirmCmd.action === "transfer"
                      ) {
                        onCommand?.("transfer", [actionMenu.user.id]);
                        closeActionMenu();
                      } else {
                        armConfirm(actionMenu.user.id, "transfer");
                      }
                    }
                  }}
                >
                  <Crown size={15} weight="bold" />
                  <span>
                    {confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "transfer"
                      ? "confirm?"
                      : "make owner"}
                  </span>
                </button>
                <button
                  type="button"
                  className={`avatar-action-item${confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "kick" ? " confirm" : ""}`}
                  onClick={() => {
                    if (
                      confirmCmd?.id === actionMenu.user.id &&
                      confirmCmd.action === "kick"
                    ) {
                      onCommand?.("kick", [actionMenu.user.id]);
                      closeActionMenu();
                    } else {
                      armConfirm(actionMenu.user.id, "kick");
                    }
                  }}
                >
                  <Boot size={15} weight="bold" />
                  <span>
                    {confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "kick"
                      ? "confirm?"
                      : ownerDirect
                        ? "kick"
                        : "vote to kick"}
                  </span>
                </button>
                <button
                  type="button"
                  className="avatar-action-item"
                  disabled={!ownerDirect}
                  title={ownerDirect ? undefined : "Pro feature — not available yet"}
                  onClick={() => {
                    if (ownerDirect) {
                      onCommand?.("mute", [actionMenu.user.id]);
                      closeActionMenu();
                    }
                  }}
                >
                  {mutedIds.includes(actionMenu.user.id) ? (
                    <Microphone size={15} weight="bold" />
                  ) : (
                    <MicrophoneSlash size={15} weight="bold" />
                  )}
                  <span>
                    {mutedIds.includes(actionMenu.user.id) ? "unmute" : "mute"}
                  </span>
                </button>
                <button
                  type="button"
                  className={`avatar-action-item danger${confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "ban" ? " confirm" : ""}`}
                  onClick={() => {
                    if (
                      confirmCmd?.id === actionMenu.user.id &&
                      confirmCmd.action === "ban"
                    ) {
                      onCommand?.("ban", [actionMenu.user.id]);
                      closeActionMenu();
                    } else {
                      armConfirm(actionMenu.user.id, "ban");
                    }
                  }}
                >
                  <Prohibit size={15} weight="bold" />
                  <span>
                    {confirmCmd?.id === actionMenu.user.id && confirmCmd.action === "ban"
                      ? "confirm?"
                      : ownerDirect
                        ? "ban"
                        : "vote to ban"}
                  </span>
                </button>
              </>
            )}
            {(inPrivateRoom && !isOwner && users.length >= 3) && (
              <>
                <button
                  type="button"
                  className="avatar-action-item"
                  onClick={() => {
                    onCommand?.("kick", [actionMenu.user.id]);
                    closeActionMenu();
                  }}
                >
                  <Boot size={15} weight="bold" />
                  <span>vote to kick</span>
                </button>
                <button
                  type="button"
                  className="avatar-action-item danger"
                  onClick={() => {
                    onCommand?.("ban", [actionMenu.user.id]);
                    closeActionMenu();
                  }}
                >
                  <Prohibit size={15} weight="bold" />
                  <span>vote to ban</span>
                </button>
              </>
            )}
            <button
              type="button"
              className={`avatar-action-item${onBlock && isBlocked?.(actionMenu.user.username) ? " active" : ""}`}
              onClick={() => {
                onBlock?.(actionMenu.user.username);
                closeActionMenu();
              }}
            >
              {onBlock && isBlocked?.(actionMenu.user.username) ? (
                <Eye size={15} weight="bold" />
              ) : (
                <EyeSlash size={15} weight="bold" />
              )}
              <span>
                {onBlock && isBlocked?.(actionMenu.user.username)
                  ? "unblock"
                  : "block"}
              </span>
            </button>
          </div>,
          document.body,
        )}

      {editing &&
        me &&
        editPosition &&
        createPortal(
          <form
            className="theater-edit"
            ref={editRef}
            onSubmit={handleRename}
            style={{
              position: "fixed",
              top: editPosition.top,
              left: editPosition.left,
            }}
          >
            <div className="theater-edit-top">
              {failedAvatars.has(me.id) ? (
                <AvatarFallback className="theater-edit-avatar" user={me} />
              ) : (
                <img
                  className="theater-edit-avatar"
                  src={avatarUrl(me)}
                  alt={me.username}
                  onError={() => onAvatarError(me.id)}
                />
              )}
              <button
                type="button"
                className={`theater-reroll-btn${syncing ? " spinning" : ""}`}
                onClick={handleReroll}
                title="Reroll avatar"
                disabled={profileSyncing}
              >
                <ArrowsClockwise size={14} weight="fill" />
              </button>
            </div>
            <div className="theater-edit-row">
              <input
                className="theater-edit-input"
                type="text"
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  setLocalError("");
                }}
                maxLength={20}
                placeholder="Your name"
                autoFocus
                onFocus={(e) => e.target.select()}
              />
              <button
                type="submit"
                className={`theater-edit-save${syncing ? " pending" : ""}`}
                disabled={pending || profileSyncing}
              >
                {syncing ? <span className="save-spinner" /> : "Save"}
              </button>
              <button
                type="button"
                className="theater-edit-cancel"
                onClick={closeEdit}
              >
                Cancel
              </button>
            </div>
            {error && <p className="theater-edit-error">{error}</p>}
          </form>,
          document.body,
        )}
      </div>

    </div>
  );
}, (prev, next) => {
  const bubbleRelevant =
    !!prev.nerdMode ||
    !!next.nerdMode;
  const bubblesEqual =
    !bubbleRelevant ||
    prev.bubbleMessages === next.bubbleMessages ||
    (prev.bubbleMessages?.length === next.bubbleMessages?.length &&
      (prev.bubbleMessages?.every(
        (m, i) => m.id === next.bubbleMessages?.[i]?.id,
      ) ??
        false));

  return (
    usersShallowEqual(prev.users, next.users) &&
    meEqual(prev.me, next.me) &&
    prev.collapsed === next.collapsed &&
    prev.nerdMode === next.nerdMode &&
    prev.scatterBubbles === next.scatterBubbles &&
    prev.scatterFullscreen === next.scatterFullscreen &&
    prev.followingUserId === next.followingUserId &&
    prev.lastError === next.lastError &&
    prev.profileSyncing === next.profileSyncing &&
    bubblesEqual
  );
});
